import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { errorText } from "./form-errors";
import { FormFields, FormSection } from "@/components/page-canvas";

export type CustomerValues = {
  name: string;
  email: string;
  phone: string;
  address: string;
};
export const customerTargets = Object.fromEntries(
  ["name", "email", "phone", "address"].map((field) => [
    field,
    `customer-${field}`,
  ]),
);
export default function CustomerFields({
  values,
  onChange,
  errors = {},
}: {
  values: Partial<CustomerValues>;
  onChange?: (field: keyof CustomerValues, value: string) => void;
  errors?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <FormSection>
      <p className="text-sm text-muted-foreground">
        {t("customers.contact_required")}
      </p>
      <FormFields paired={false}>
        {(["name", "email", "phone", "address"] as const).map((field) => {
          const fieldProps = {
            id: `customer-${field}`,
            name: `customer[${field}]`,
            ...(onChange
              ? {
                  value: values[field] || "",
                  onChange: (
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => onChange(field, event.target.value),
                }
              : { defaultValue: values[field] || "" }),
            "aria-invalid": !!errors[field],
            "aria-describedby": errors[field]
              ? `customer-${field}-error`
              : field === "phone"
                ? "customer-phone-help"
                : undefined,
            autoComplete:
              field === "phone"
                ? "tel"
                : field === "address"
                  ? "street-address"
                  : field,
          };
          return (
            <div key={field} className="space-y-2">
              <Label htmlFor={fieldProps.id}>{t(`customers.${field}`)}</Label>
              {field === "address" ? (
                <Textarea {...fieldProps} />
              ) : (
                <Input
                  {...fieldProps}
                  type={
                    field === "email"
                      ? "email"
                      : field === "phone"
                        ? "tel"
                        : "text"
                  }
                  autoFocus={field === "name" && !Object.keys(errors).length}
                  required={field === "name"}
                />
              )}
              {field === "phone" && (
                <p
                  id="customer-phone-help"
                  className="text-sm text-muted-foreground"
                >
                  {t("customers.phone_help")}
                </p>
              )}
              {errors[field] ? (
                <p
                  id={`customer-${field}-error`}
                  className="text-sm text-destructive"
                >
                  {errorText(errors[field])}
                </p>
              ) : null}
            </div>
          );
        })}
      </FormFields>
    </FormSection>
  );
}
